import React from "react";
import { BatchTableRow } from "../types/batchSession.types";
import { StatusPill, RecordingIndicator } from "./Pills";

const PAGE_SIZE = 15;

/* ── Helper: parse "HH:MM" to minutes from midnight ────────────────────────── */
function parseTimeToMinutes(timeStr: string): number {
  if (!timeStr) return -1;
  const match = timeStr.trim().match(/^(\d{1,2}):(\d{2})$/);
  if (!match) return -1;
  const hours = parseInt(match[1], 10);
  const mins = parseInt(match[2], 10);
  if (hours < 0 || hours > 23 || mins < 0 || mins > 59) return -1;
  return hours * 60 + mins;
}

/* ── Helper: current IST time in minutes from midnight ─────────────────────── */
function getCurrentTimeMinutes(): number {
  const now = new Date();
  const istOffset = 5.5 * 60;
  const utcMinutes = now.getUTCHours() * 60 + now.getUTCMinutes();
  return (utcMinutes + istOffset) % (24 * 60);
}

/* ── Indicator color logic ─────────────────────────────────────────────────── */
type IndicatorColor = "green" | "red" | "gray";

// function getIndicatorColor(b: BatchTableRow, currentMinutes: number): IndicatorColor {
//   const batchMinutes = parseTimeToMinutes(b.batchTiming);

//   // No timing set → gray (default)
//   if (batchMinutes === -1) return "gray";

//   // Session hasn't started yet (current time before batch timing) → gray
//   if (currentMinutes < batchMinutes) return "gray";

//   // Session has ended/completed → back to gray
//   const sessionStatus = (b.latestSessionStatus || "").toLowerCase();
//   if (sessionStatus === "ended" || sessionStatus === "completed") return "gray";

//   // ── Session time reached, check live conditions ──

//   // Check trainer status
//   const trainerStatus = (b.latestSessionTrainer?.status || "").toLowerCase();
//   const trainerActive = trainerStatus === "clicked" || trainerStatus === "joined" || trainerStatus === "attended";

//   // Check student status — at least one clicked/joined
//   const studentActive = b.latestSessionStudents.some((s) => {
//     const st = s.status.toLowerCase();
//     return st === "clicked" || st === "joined" || st === "attended";
//   });

//   // Check recording status
//   const recStatus = (b.latestSessionRecordingStatus || "").toLowerCase();
//   const recActive = recStatus === "recording" || recStatus === "available";

//   // All three conditions met → green
//   if (trainerActive && studentActive && recActive) return "green";

//   // Session time reached but not all conditions met → red
//   return "red";
// }
// ─── Updated getIndicatorColor ──────────────────────────────────────────────
// function getIndicatorColor(b: BatchTableRow, currentMinutes: number): IndicatorColor {
//   const batchMinutes = parseTimeToMinutes(b.batchTiming);

//   if (batchMinutes === -1) return "gray";
//   if (currentMinutes < batchMinutes) return "gray";

//   //  NEW: Grey out after 1.5 hours (90 minutes) past batch start
//   if (currentMinutes > batchMinutes + 90) return "gray";

//   const sessionStatus = (b.latestSessionStatus || "").toLowerCase();
//   if (sessionStatus === "ended" || sessionStatus === "completed") return "gray";

//   const trainerStatus = (b.latestSessionTrainer?.status || "").toLowerCase();
//   const trainerActive =
//     trainerStatus === "clicked" || trainerStatus === "joined" || trainerStatus === "attended";

//   const studentActive = b.latestSessionStudents.some((s) => {
//     const st = s.status.toLowerCase();
//     return st === "clicked" || st === "joined" || st === "attended";
//   });

//   const recStatus = (b.latestSessionRecordingStatus || "").toLowerCase();
//   const recActive = recStatus === "recording" || recStatus === "available";

//   if (trainerActive && studentActive && recActive) return "green";
//   return "red";
// }

function getIndicatorColor(b: BatchTableRow, currentMinutes: number): IndicatorColor {
  const batchMinutes = parseTimeToMinutes(b.batchTiming);

  if (batchMinutes === -1) return "gray";
  if (currentMinutes < batchMinutes) return "gray";

  //  Grey out when session is ended or completed (removed the 90-min rule)
  const sessionStatus = (b.latestSessionStatus || "").toLowerCase();
  if (sessionStatus === "ended" || sessionStatus === "completed") return "gray";

  const trainerStatus = (b.latestSessionTrainer?.status || "").toLowerCase();
  const trainerActive =
    trainerStatus === "clicked" || trainerStatus === "joined" || trainerStatus === "attended";

  const studentActive = b.latestSessionStudents.some((s) => {
    const st = s.status.toLowerCase();
    return st === "clicked" || st === "joined" || st === "attended";
  });

  const recStatus = (b.latestSessionRecordingStatus || "").toLowerCase();
  const recActive = recStatus === "recording" || recStatus === "available";

  if (trainerActive && studentActive && recActive) return "green";
  return "red";
}
/* ── Schedule Type badge styling ────────────────────────────────────────── */
function getScheduleBadgeClasses(schedule: string): string {
  switch (schedule) {
    case "Weekdays":
      return "bg-sky-50 text-sky-700 border-sky-200";
    case "Weekends":
      return "bg-purple-50 text-purple-700 border-purple-200";
    case "Flexible":
      return "bg-amber-50 text-amber-700 border-amber-200";
    case "All Days":
      return "bg-teal-50 text-teal-700 border-teal-200";
    default:
      return "bg-slate-50 text-slate-400 border-slate-200";
  }
}

const INDICATOR_STYLES: Record<IndicatorColor, { bg: string; title: string }> = {
  green: { bg: "bg-emerald-500", title: "All active: Trainer joined, Students joined, Recording on" },
  red:   { bg: "bg-red-500",     title: "Alert: Session started but conditions not fully met" },
  gray:  { bg: "bg-slate-300",   title: "Session not started or ended" },
};

interface Props {
  batches: BatchTableRow[];
  onViewBatch: (batchNo: string) => void;
  page: number;
  onPageChange: (p: number) => void;
}

export default function BatchesTable({
  batches,
  onViewBatch,
  page,
  onPageChange,
}: Props) {
  const currentMinutes = getCurrentTimeMinutes();
  const totalPages = Math.ceil(batches.length / PAGE_SIZE);
  const paginated = batches.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  if (batches.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-20 gap-2">
        <div className="w-14 h-14 rounded-full bg-slate-100 flex items-center justify-center mb-2">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="w-7 h-7 text-slate-400"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
        </div>
        <p className="text-base font-semibold text-slate-600">No batches found</p>
        <p className="text-sm text-slate-400">Try adjusting your filters</p>
      </div>
    );
  }

  return (
    <div>
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="bg-slate-50 border-b border-slate-200">
              <th className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wider px-4 py-3 whitespace-nowrap">
                Batch Number
              </th>
              <th className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wider px-4 py-3 whitespace-nowrap">
                Course Name
              </th>
              <th className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wider px-4 py-3 whitespace-nowrap">
                Batch Timing
              </th>
              <th className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wider px-4 py-3 whitespace-nowrap">
                Schedule
              </th>
              <th className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wider px-4 py-3 whitespace-nowrap">
                Trainer
              </th>
              <th className="text-center text-xs font-semibold text-slate-500 uppercase tracking-wider px-4 py-3 whitespace-nowrap">
                Students
              </th>
               <th className="text-center text-xs font-semibold text-slate-500 uppercase tracking-wider px-4 py-3 whitespace-nowrap">
                Unknown
              </th>
              <th className="text-center text-xs font-semibold text-slate-500 uppercase tracking-wider px-4 py-3 whitespace-nowrap">
                Recording
              </th>
              <th className="text-center text-xs font-semibold text-slate-500 uppercase tracking-wider px-4 py-3 whitespace-nowrap">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {paginated.map((b) => {
              // Compute student counts from today's session
              const attendedStudents = b.latestSessionStudents.filter((s) => {
                const st = s.status.toLowerCase();
                return st === "joined" || st === "attended";
              }).length;
              const totalSessionStudents = b.latestSessionStudents.length;
              const hasLiveSession =
                b.latestSessionStatus.toLowerCase() === "live" ||
                b.latestSessionStatus.toLowerCase() === "active";

              // Compute indicator color
              const indicatorColor = getIndicatorColor(b, currentMinutes);
              const indicatorStyle = INDICATOR_STYLES[indicatorColor];

              return (
                <tr
                  key={b.batchNo}
                  
                  className={`transition-colors duration-150 ${
                    hasLiveSession
                      ? "bg-emerald-50/30 hover:bg-emerald-50/50"
                      : "bg-white hover:bg-slate-50/60"
                  }`}
                >
                  {/* Batch Number with Status Indicator */}
                  <td className="px-4 py-3 whitespace-nowrap">
                    <div className="flex items-center gap-2.5">
                      {/* Vertical Status Indicator Bar */}
                      <div
                        className={`w-1 h-7 rounded-full flex-shrink-0 transition-colors duration-300 ${indicatorStyle.bg} ${indicatorColor === "red" ? "animate-red-blink" : ""}`}
                        title={indicatorStyle.title}
                      />
                      <span className="font-bold text-[#221E45] text-sm">
                        {b.batchNo}
                      </span>
                      {hasLiveSession && (
                        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-bold bg-emerald-100 text-emerald-700 border border-emerald-200 uppercase">
                          <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                          Live
                        </span>
                      )}
                    </div>
                  </td>

                  {/* Course Name */}
                  <td className="px-4 py-3 whitespace-nowrap">
                    <span className="text-xs text-slate-600 font-medium">
                      {b.courseName || "—"}
                    </span>
                  </td>

                  {/* Batch Timing */}
                  <td className="px-4 py-3 whitespace-nowrap">
                    {b.batchTiming ? (
                      <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium bg-violet-50 text-violet-700 border border-violet-200">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="w-3 h-3"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                          strokeWidth={2}
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                          />
                        </svg>
                        {b.batchTiming}
                      </span>
                    ) : (
                      <span className="text-slate-300 text-xs">—</span>
                    )}
                  </td>

                  {/* Schedule Type */}
                  <td
                    className="px-4 py-3 whitespace-nowrap"
                    title={
                      b.scheduleStatus === "Flexible" ? b.scheduleDetails : undefined
                    }
                  >
                    {b.scheduleStatus ? (
                      <span
                        className={`inline-flex items-center px-2 py-1 rounded-md text-xs font-medium border ${getScheduleBadgeClasses(
                          b.scheduleStatus,
                        )}`}
                      >
                        {b.scheduleStatus}
                      </span>
                    ) : (
                      <span className="text-slate-300 text-xs">—</span>
                    )}
                  </td>

                  {/* Trainer Status — inline from today's session */}
                  <td className="px-4 py-3 whitespace-nowrap">
                    {b.latestSessionTrainer ? (
                      <div className="space-y-1">
                        <span className="text-xs font-semibold text-slate-800">
                          {b.latestSessionTrainer.name}
                        </span>
                        <StatusPill status={b.latestSessionTrainer.status} />
                      </div>
                    ) : (
                      <div className="space-y-0.5">
                        <span className="text-xs font-semibold text-slate-800">
                          {b.trainerName || "—"}
                        </span>
                        {b.latestSessionStatus ? (
                          <div>
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-slate-100 text-slate-400 border border-slate-200">
                              No session today
                            </span>
                          </div>
                        ) : null}
                      </div>
                    )}
                  </td>

                  {/* Students — compact pill with hover tooltip */}
                  <td className="px-4 py-3 text-center whitespace-nowrap">
                    {b.latestSessionStudents.length > 0 ? (
                      <div className="group relative inline-block">
                        <span
                          className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold border cursor-default ${
                            attendedStudents === totalSessionStudents &&
                            totalSessionStudents > 0
                              ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                              : attendedStudents > 0
                              ? "bg-blue-50 text-blue-700 border-blue-200"
                              : "bg-red-50 text-red-600 border-red-200"
                          }`}
                        >
                          {attendedStudents}/{totalSessionStudents}
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="w-3 h-3"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                            strokeWidth={2}
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z"
                            />
                          </svg>
                        </span>
                        {/* Hover Tooltip — student names + status */}
                        {/* <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-2 bg-slate-800 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none z-20 shadow-lg min-w-max">
                          <p className="font-semibold mb-1 text-slate-300 border-b border-slate-600 pb-1">
                            Today's Session ({attendedStudents}/
                            {totalSessionStudents})
                          </p>
                          {b.latestSessionStudents.map((st, i) => {
                            const joined =
                              st.status.toLowerCase() === "joined" ||
                              st.status.toLowerCase() === "attended";
                            return (
                              <div
                                key={i}
                                className="flex items-center gap-2 py-0.5"
                              >
                                <span
                                  className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${
                                    joined ? "bg-emerald-400" : "bg-red-400"
                                  }`}
                                />
                                <span className="font-medium">{st.name}</span>
                                <span
                                  className={`text-xs ${
                                    joined
                                      ? "text-emerald-400"
                                      : "text-red-400"
                                  }`}
                                >
                                  {st.status}
                                </span>
                              </div>
                            );
                          })}
                          <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-slate-800"></div>
                        </div> */}
                        {/* Hover Tooltip — student names + status + unknown participants */}
<div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-2 bg-slate-800 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none z-20 shadow-lg min-w-max">
  <p className="font-semibold mb-1 text-slate-300 border-b border-slate-600 pb-1">
    Today's Session ({attendedStudents}/{totalSessionStudents})
  </p>
  {b.latestSessionStudents.map((st, i) => {
    const joined =
      st.status.toLowerCase() === "joined" ||
      st.status.toLowerCase() === "attended";
    return (
      <div key={i} className="flex items-center gap-2 py-0.5">
        <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${joined ? "bg-emerald-400" : "bg-red-400"}`} />
        <span className="font-medium">{st.name}</span>
        <span className={`text-xs ${joined ? "text-emerald-400" : "text-red-400"}`}>
          {st.status}
        </span>
      </div>
    );
  })}


  <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-slate-800" />
</div>
                      </div>
                    ) : (
                      <div className="group relative inline-block">
                        <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-blue-50 text-blue-700 border border-blue-200 cursor-default">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="w-3 h-3"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                            strokeWidth={2}
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z"
                            />
                          </svg>
                          {b.totalStudents}
                        </span>
                        {/* Hover Tooltip — all enrolled students */}
                        {b.students.length > 0 && (
                          <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-2 bg-slate-800 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none z-20 shadow-lg min-w-max">
                            <p className="font-semibold mb-1 text-slate-300 border-b border-slate-600 pb-1">
                              Enrolled Students ({b.totalStudents})
                            </p>
                            {b.students.map((st, i) => (
                              <div key={i} className="py-0.5">
                                <span className="font-medium">{st.name}</span>
                                <span className="text-slate-400 ml-1">
                                  ({st.email})
                                </span>
                              </div>
                            ))}
                            <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-slate-800"></div>
                          </div>
                        )}
                      </div>
                    )}
                  </td>
                  {/* Unknown Participants Column */}
                  <td className="px-4 py-3 text-center whitespace-nowrap">
                    {(b.latestSessionUnknownParticipants?.length ?? 0) > 0 ? (
                      <div className="group relative inline-block">
                        <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold border cursor-default bg-amber-50 text-amber-700 border-amber-200">
                          {b.latestSessionUnknownParticipants!.length}
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="w-3 h-3"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                            strokeWidth={2}
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                            />
                          </svg>
                        </span>
                        {/* Hover Tooltip — unknown participant names + duration */}
                        <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-2 bg-slate-800 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none z-20 shadow-lg min-w-max">
                          <p className="font-semibold mb-1 text-amber-400 border-b border-slate-600 pb-1 flex items-center gap-1">
                            <span className="w-1.5 h-1.5 rounded-full bg-amber-400 inline-block" />
                            Unknown Participants ({b.latestSessionUnknownParticipants!.length})
                          </p>
                          {b.latestSessionUnknownParticipants!.map((u, i) => (
                            <div key={i} className="flex items-center gap-2 py-0.5">
                              <span className="w-1.5 h-1.5 rounded-full flex-shrink-0 bg-amber-400" />
                              <span className="font-medium">{u.displayName}</span>
                              <span className="text-amber-400 text-xs">
                                Joined · {Math.round(u.durationSec / 60)}m
                              </span>
                            </div>
                          ))}
                          <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-slate-800" />
                        </div>
                      </div>
                    ) : (
                      <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-slate-50 text-slate-400 border border-slate-200 cursor-default">
                        0
                      </span>
                    )}
                  </td>

                  {/* Recording — inline from today's session */}
                  <td className="px-4 py-3 text-center whitespace-nowrap">
                    <RecordingIndicator
                      status={b.latestSessionRecordingStatus}
                      recordings={b.latestSessionRecordings}
                    />
                  </td>

                  {/* Actions */}
                  <td className="px-4 py-3 text-center whitespace-nowrap">
                    <div className="flex items-center justify-center gap-2">
                      {/* View Sessions button */}
                      <button
                        onClick={() => onViewBatch(b.batchNo)}
                        className="inline-flex items-center justify-center w-7 h-7 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-600 hover:text-slate-800 transition-colors"
                        title="View Sessions"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="w-3.5 h-3.5"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                          strokeWidth={2}
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                          />
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                          />
                        </svg>
                      </button>

                      {/* Meeting link */}
                      {b.meetingLink ? (
                        <a
                          href={b.meetingLink}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center justify-center w-7 h-7 rounded-lg bg-[#221E45] hover:bg-[#221E45]/90 text-white transition-colors"
                          title="Join Meeting"
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="w-3.5 h-3.5"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                            strokeWidth={2}
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                            />
                          </svg>
                        </a>
                      ) : (
                        <span className="text-slate-300 text-xs">—</span>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="px-6 py-3.5 bg-slate-50 border-t border-slate-200 flex items-center justify-between">
          <p className="text-xs text-slate-500">
            Showing{" "}
            <span className="font-semibold text-slate-700">
              {(page - 1) * PAGE_SIZE + 1}–
              {Math.min(page * PAGE_SIZE, batches.length)}
            </span>{" "}
            of{" "}
            <span className="font-semibold text-slate-700">{batches.length}</span>{" "}
            batches
          </p>
          <div className="flex items-center gap-1">
            <button
              onClick={() => onPageChange(Math.max(1, page - 1))}
              disabled={page === 1}
              className="flex items-center justify-center w-8 h-8 rounded-lg border border-slate-200 bg-white text-slate-600 hover:bg-slate-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="w-3.5 h-3.5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2.5}
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            {Array.from({ length: totalPages }, (_, i) => i + 1)
              .filter(
                (p) => p === 1 || p === totalPages || Math.abs(p - page) <= 1,
              )
              .map((p, idx, arr) => (
                <React.Fragment key={p}>
                  {idx > 0 && p - arr[idx - 1] > 1 && (
                    <span className="w-8 h-8 flex items-center justify-center text-xs text-slate-400">
                      …
                    </span>
                  )}
                  <button
                    onClick={() => onPageChange(p)}
                    className={`w-8 h-8 rounded-lg text-xs font-semibold border transition-colors ${
                      page === p
                        ? "bg-[#221E45] text-white border-[#221E45]"
                        : "bg-white text-slate-600 border-slate-200 hover:bg-slate-100"
                    }`}
                  >
                    {p}
                  </button>
                </React.Fragment>
              ))}
            <button
              onClick={() => onPageChange(Math.min(totalPages, page + 1))}
              disabled={page === totalPages}
              className="flex items-center justify-center w-8 h-8 rounded-lg border border-slate-200 bg-white text-slate-600 hover:bg-slate-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="w-3.5 h-3.5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2.5}
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
